import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import moment from 'moment';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs';
import { inputTypesEnum } from '../../../../../../form-builder/enums/input-type';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { DatePickerComponent } from '../../../../../../ui-components/lib/date-picker/date-picker.component';
import {
  DateFormat,
  DateParseFormats,
} from '../../../../../../ui-components/lib/date-picker/date-picker.constant';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import {
  AssignList,
  LookupOptionItem,
} from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { MultiLookupFieldComponent } from '../../../../../../ui-components/lib/multi-lookup-field/multi-lookup-field.component';
import { MultiSelectComponent } from '../../../../../../ui-components/lib/multi-select/multi-select.component';
import {
  OptionItem,
  ToggleOption,
} from '../../../../../../ui-components/lib/option.interface';
import { RadioButtonComponent } from '../../../../../../ui-components/lib/radio-button/radio-button.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { localized } from '../../custom-workflow/custom-workflow.model';
import { ConditionOperatorEnum } from '../enums/condition-operator.enum';
import { ConditionValueKindEnum } from '../enums/condition-value-kind.enum';
import {
  ConditionMatch,
  ConditionRule,
  ConditionSet,
  ReflectionField,
  ReflectionLabeledValue,
} from '../reflections.models';
import {
  REFLECTION_USER_LOOKUP_API,
  reflectionUserLookupAssignList,
  userLookupOptions,
} from '../reflection-user-lookup';

const MATCH_OPTIONS: ConditionMatch[] = ['all', 'any'];

/** Operators that compare nothing, so their rule has no value. */
const VALUELESS_OPERATORS = new Set<string>([
  ConditionOperatorEnum.IS_EMPTY,
  ConditionOperatorEnum.IS_NOT_EMPTY,
  ConditionOperatorEnum.IS_CHECKED,
  ConditionOperatorEnum.IS_NOT_CHECKED,
]);

const NUMBER_TYPES = new Set<string>([
  inputTypesEnum.NUMBER_INPUT_FIELD,
  inputTypesEnum.MONEY,
]);

/**
 * Lookup fields other than users have no record picker in the builder yet, so their rules can only test whether
 * a value is there.
 */
const GENERIC_LOOKUP_TYPES = new Set<string>([
  inputTypesEnum.LOOKUP,
  inputTypesEnum.PAGINATED_SELECT,
  inputTypesEnum.EXPANDABLE_SELECT,
]);
const EMPTY_OPERATORS = new Set<string>([
  ConditionOperatorEnum.IS_EMPTY,
  ConditionOperatorEnum.IS_NOT_EMPTY,
]);

/** `is` / `is_not` on a user field compare one user: the lookup keeps a single slot. */
const SINGLE_USER_SIZE = { min_size: 1, max_size: 1 };

const DATE_KINDS = new Set([
  ConditionValueKindEnum.DATE,
  ConditionValueKindEnum.DATE_TIME,
]);

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && !value.length);

const takesValue = (operator: unknown) =>
  !!operator && !VALUELESS_OPERATORS.has(operator as string);

/** A rule whose operator compares something needs a value. */
const valueRequiredValidator = (
  control: AbstractControl
): ValidationErrors | null =>
  takesValue(control.parent?.get('operator')?.value) && isBlank(control.value)
    ? { required: true }
    : null;

/** Which input edits the value of a rule on `field` with `operator`. */
function valueKindFor(
  field: ReflectionField | undefined,
  operator: string | null
): ConditionValueKindEnum {
  if (!field) return ConditionValueKindEnum.TEXT;
  if (field.type === inputTypesEnum.LOOKUP_USER)
    return ConditionValueKindEnum.USER;
  if (NUMBER_TYPES.has(field.type)) return ConditionValueKindEnum.NUMBER;
  if (field.type === inputTypesEnum.DATE_PICKER)
    return ConditionValueKindEnum.DATE;
  if (field.type === inputTypesEnum.DATE_TIME_PICKER)
    return ConditionValueKindEnum.DATE_TIME;
  if (field.options.length)
    return operator === ConditionOperatorEnum.IS_ANY_OF
      ? ConditionValueKindEnum.MULTI_SELECT
      : ConditionValueKindEnum.SELECT;
  return ConditionValueKindEnum.TEXT;
}

/** Whether a rule's value is a list: `is_any_of` on a field with options or on a user field. */
function takesList(
  field: ReflectionField | undefined,
  operator: string | null
): boolean {
  const kind = valueKindFor(field, operator);
  return (
    kind === ConditionValueKindEnum.MULTI_SELECT ||
    (kind === ConditionValueKindEnum.USER &&
      operator === ConditionOperatorEnum.IS_ANY_OF)
  );
}

/** The ids a users lookup holds: its FormArray value, without the empty slots. */
const pickedIds = (value: unknown): number[] =>
  (Array.isArray(value) ? value : []).filter(
    (id): id is number => id !== null && id !== undefined && id !== ''
  );

const ISO_DATE = 'YYYY-MM-DD';
const ISO_DATE_TIME = 'YYYY-MM-DD HH:mm:ss';
const PICKER_DATE = 'DD-MMM-yyyy';
const PICKER_PARSE_FORMATS = [moment.ISO_8601, ...DateParseFormats];

/**
 * A condition's date value, as `lib-date-picker` wrote it, in the ISO form the backend compares:
 * `YYYY-MM-DD` for dates, `YYYY-MM-DD HH:mm:ss` in UTC for date-times (the picker writes those in UTC).
 */
export function pickerValueToIso(
  value: unknown,
  kind: ConditionValueKindEnum
): string | null {
  if (typeof value !== 'string' || !value) return null;
  const parsed =
    kind === ConditionValueKindEnum.DATE_TIME
      ? moment.utc(value, PICKER_PARSE_FORMATS)
      : moment(value, PICKER_PARSE_FORMATS);
  return parsed.isValid()
    ? parsed.format(
        kind === ConditionValueKindEnum.DATE_TIME ? ISO_DATE_TIME : ISO_DATE
      )
    : null;
}

/** An ISO condition value, in the form `lib-date-picker` keeps in its control (and can display). */
export function isoToPickerValue(
  value: unknown,
  kind: ConditionValueKindEnum
): string | null {
  if (typeof value !== 'string' || !value) return null;
  const isDateTime = kind === ConditionValueKindEnum.DATE_TIME;
  const parsed = isDateTime
    ? moment.utc(value, [ISO_DATE_TIME, moment.ISO_8601])
    : moment(value, [ISO_DATE, moment.ISO_8601]);
  return parsed.isValid()
    ? parsed.format(isDateTime ? DateFormat : PICKER_DATE)
    : null;
}

/**
 * The field each rule's operator and value were set for. `lib-select` emits `changeOption` for the field it already
 * holds (once after init and again on every write), so only a different field key counts as the admin changing it.
 */
const ruleFieldKeys = new WeakMap<FormGroup, string | null>();

export function buildConditionRule(rule?: Partial<ConditionRule>): FormGroup {
  const group = new FormGroup({
    field_key: new FormControl<string | null>(
      rule?.field_key ?? null,
      Validators.required
    ),
    operator: new FormControl<ConditionOperatorEnum | null>(
      rule?.operator ?? null,
      Validators.required
    ),
    value: new FormControl<unknown>(
      rule?.value ?? null,
      valueRequiredValidator
    ),
  });
  ruleFieldKeys.set(group, rule?.field_key ?? null);
  return group;
}

/** `{match, rules}` form group of a reflection's conditions; no rules means the reflection always runs. */
export function buildConditionsGroup(set?: ConditionSet): FormGroup {
  return new FormGroup({
    match: new FormControl<ConditionMatch>(set?.match ?? 'all', {
      nonNullable: true,
    }),
    rules: new FormArray(
      (set?.rules ?? []).map(rule => buildConditionRule(rule))
    ),
  });
}

/** What the users lookup of a user-field rule binds to. */
export interface ConditionUserPickerView {
  /**
   * Twin of the rule's `value`: the lookup swaps its `value` control for a FormArray of picked ids, which is
   * written back to the rule as one id (`is`, `is_not`) or a list (`is_any_of`).
   */
  group: FormGroup;
  /** Changes when the lookup must start over, so the template renders a fresh one. */
  key: number;
  /** One slot for a single user, unlimited for `is_any_of`. */
  size: { min_size: number; max_size: number } | null;
  /** The users the lookup opens with (it reads them once). */
  selected: LookupOptionItem[];
}

/** One rule with what the template needs to render it. */
export interface ConditionRowView {
  group: FormGroup;
  index: number;
  operatorOptions: OptionItem[];
  showValue: boolean;
  valueKind: ConditionValueKindEnum;
  valueOptions: OptionItem[];
  /**
   * Date and date-time rules only: what `lib-date-picker` binds to, holding the value in the picker's format
   * while the rule's `value` control keeps the ISO form.
   */
  pickerGroup: FormGroup | null;
  /** User-field rules only. */
  userPicker: ConditionUserPickerView | null;
}

type RawRule = {
  field_key: string | null;
  operator: ConditionOperatorEnum | null;
  value: unknown;
};

/** "Only if" section of the reflection editor: rules (field, operator, value) joined by All of / Any of. */
@Component({
  selector: 'lib-reflection-conditions',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ButtonComponent,
    DatePickerComponent,
    InputComponent,
    MultiLookupFieldComponent,
    MultiSelectComponent,
    RadioButtonComponent,
    SelectComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './reflection-conditions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionConditionsComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  /** The picker-format twin of each date rule's `value` control. */
  private readonly pickerGroups = new WeakMap<FormGroup, FormGroup>();
  /** The users-lookup twin of each user rule's `value` control. */
  private readonly userGroups = new WeakMap<FormGroup, FormGroup>();
  /** How many times each user rule's lookup started over; part of its render key. */
  private readonly userPickerResets = new WeakMap<FormGroup, number>();
  /** Bumped on every reset, so the rows (and the render keys) are recomputed. */
  private readonly userPickerVersion = signal(0);

  /** `{match: FormControl<'all' | 'any'>, rules: FormArray}`, see `buildConditionsGroup`. */
  group = input.required<FormGroup>();
  fields = input.required<ReflectionField[]>();
  /** Operators per form field type, from the operators lookup (labels localised by the backend). */
  operators = input.required<Record<string, ReflectionLabeledValue[]>>();
  /** Prefix of the element ids, so several builders on a page stay unique. */
  idPrefix = input('reflection_condition');
  /**
   * Names of the users the stored rules refer to, by id (rules keep ids only). Must be set before the users lookup
   * renders, as it reads its selection once.
   */
  userLabels = input<Record<number, string>>({});

  readonly ConditionValueKind = ConditionValueKindEnum;

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  /** Raw value of the group, re-read on every change (and when the group input is swapped). */
  private readonly groupValue = toSignal(
    toObservable(this.group).pipe(
      switchMap(group =>
        group.valueChanges.pipe(
          startWith(null),
          map(() => group.getRawValue() as { rules: RawRule[] })
        )
      ),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ),
    { initialValue: null }
  );
  private readonly rawRules = computed(
    () =>
      (
        this.groupValue() ??
        (this.group().getRawValue() as { rules: RawRule[] })
      ).rules
  );

  private readonly rulesArray = computed(
    () => this.group().get('rules') as FormArray
  );

  private readonly fieldsByKey = computed(
    () => new Map(this.fields().map(field => [field.value, field]))
  );

  readonly fieldOptions = computed<OptionItem[]>(() =>
    this.fields().map(field => ({
      value: field.value,
      label: localized(field.label, this.lang()),
    }))
  );

  readonly matchOptions = computed<ToggleOption[]>(() => {
    this.lang();
    return MATCH_OPTIONS.map(match => ({
      value: match,
      label: this.transloco.translate(`reflection_conditions_match_${match}`),
    }));
  });

  readonly userLookupApi = REFLECTION_USER_LOOKUP_API;
  readonly userLookupAssignList = computed<AssignList>(() => {
    this.lang();
    return reflectionUserLookupAssignList(key => this.transloco.translate(key));
  });

  /** Operators the builder offers per field type: the lookup's, narrowed to the empty checks for generic lookups. */
  private readonly allowedOperators = computed(
    () =>
      Object.fromEntries(
        Object.entries(this.operators()).map(([type, operators]) => [
          type,
          GENERIC_LOOKUP_TYPES.has(type)
            ? operators.filter(operator => EMPTY_OPERATORS.has(operator.value))
            : operators,
        ])
      ) as Record<string, ReflectionLabeledValue[]>
  );

  /** Operator options (labelled in the active language) per field type. */
  private readonly operatorOptionsByType = computed(() => {
    return Object.fromEntries(
      Object.entries(this.allowedOperators()).map(([type, operators]) => [
        type,
        operators.map(operator => ({
          value: operator.value,
          label: operator.label,
        })),
      ])
    ) as Record<string, OptionItem[]>;
  });

  readonly rows = computed<ConditionRowView[]>(() => {
    const raw = this.rawRules();
    const fields = this.fieldsByKey();
    const operatorOptions = this.operatorOptionsByType();
    const lang = this.lang();
    const labels = this.userLabels();
    this.userPickerVersion();
    const controls = this.rulesArray().controls as FormGroup[];
    return controls.map((group, index) => {
      const rule = raw[index];
      const field = rule?.field_key ? fields.get(rule.field_key) : undefined;
      const valueKind = valueKindFor(field, rule?.operator ?? null);
      return {
        group,
        index,
        operatorOptions: field ? (operatorOptions[field.type] ?? []) : [],
        showValue: takesValue(rule?.operator),
        valueKind,
        valueOptions: (field?.options ?? []).map(option => ({
          value: option.value,
          label: localized(option.label, lang) || option.value,
        })),
        pickerGroup: DATE_KINDS.has(valueKind) ? this.pickerGroup(group) : null,
        userPicker:
          valueKind === ConditionValueKindEnum.USER
            ? {
                group: this.userGroup(group),
                key: this.userPickerResets.get(group) ?? 0,
                size:
                  rule?.operator === ConditionOperatorEnum.IS_ANY_OF
                    ? null
                    : SINGLE_USER_SIZE,
                selected: userLookupOptions(
                  Array.isArray(rule?.value) ? rule.value : [rule?.value],
                  labels
                ),
              }
            : null,
      };
    });
  });

  private kindOf(rule: FormGroup): ConditionValueKindEnum {
    const { field_key, operator } = rule.getRawValue() as RawRule;
    return valueKindFor(this.fieldsByKey().get(field_key ?? ''), operator);
  }

  /**
   * The picker-format twin of a date rule's `value`, kept in step both ways: picking a date writes its ISO form
   * to the rule, and a value set on the rule (restore, reset) is shown in the picker.
   */
  private pickerGroup(rule: FormGroup): FormGroup {
    const existing = this.pickerGroups.get(rule);
    if (existing) return existing;

    const valueControl = rule.get('value') as FormControl;
    const picker = new FormControl<string | null>(
      isoToPickerValue(valueControl.value, this.kindOf(rule))
    );
    picker.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        valueControl.setValue(pickerValueToIso(value, this.kindOf(rule)));
        valueControl.markAsDirty();
      });
    valueControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const shown = isoToPickerValue(value, this.kindOf(rule));
        if (shown !== picker.value)
          picker.setValue(shown, { emitEvent: false });
      });

    const group = new FormGroup({ value: picker });
    this.pickerGroups.set(rule, group);
    return group;
  }

  /**
   * The users-lookup twin of a user rule's `value`. The lookup replaces the twin's control with a FormArray of
   * picked ids; every change is written to the rule as one id or, for `is_any_of`, the list.
   */
  private userGroup(rule: FormGroup): FormGroup {
    const existing = this.userGroups.get(rule);
    if (existing) return existing;

    const group = new FormGroup<Record<string, AbstractControl>>({
      value: new FormControl(null),
    });
    group.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const { field_key, operator } = rule.getRawValue() as RawRule;
        const ids = pickedIds(group.getRawValue()['value']);
        const value = takesList(
          this.fieldsByKey().get(field_key ?? ''),
          operator
        )
          ? ids
          : (ids[0] ?? null);
        const control = rule.get('value') as FormControl;
        if (JSON.stringify(value) === JSON.stringify(control.value)) return;
        control.setValue(value);
        control.markAsDirty();
      });
    this.userGroups.set(rule, group);
    return group;
  }

  /** Renders a fresh users lookup for the rule (it reads its selection only once). */
  private resetUserPicker(rule: FormGroup): void {
    this.userPickerResets.set(rule, (this.userPickerResets.get(rule) ?? 0) + 1);
    this.userPickerVersion.update(version => version + 1);
  }

  /** With a single rule there is nothing to join. */
  readonly showMatch = computed(() => this.rows().length >= 2);

  addRule(): void {
    this.rulesArray().push(buildConditionRule());
    this.rulesArray().markAsDirty();
  }

  removeRule(index: number): void {
    this.rulesArray().removeAt(index);
    this.rulesArray().markAsDirty();
  }

  /** A new field starts over: its first allowed operator and no value. */
  onFieldChange(index: number): void {
    const rule = this.rulesArray().at(index) as FormGroup;
    const fieldKey: string | null = rule.get('field_key')?.value ?? null;
    if (ruleFieldKeys.get(rule) === fieldKey) return;
    ruleFieldKeys.set(rule, fieldKey);
    const field = this.fieldsByKey().get(fieldKey ?? '');
    const first = field
      ? this.allowedOperators()[field.type]?.[0]?.value
      : undefined;
    rule.patchValue({ operator: first ?? null, value: null });
    rule.get('value')?.updateValueAndValidity();
    this.resetUserPicker(rule);
  }

  /** Keeps the value in the shape the operator expects: none, a list for `is_any_of`, or a single value. */
  onOperatorChange(index: number): void {
    const rule = this.rulesArray().at(index) as FormGroup;
    const { field_key, operator, value } = rule.getRawValue() as RawRule;
    const control = rule.get('value') as FormControl;
    if (!takesValue(operator)) {
      control.setValue(null);
    } else {
      const field = this.fieldsByKey().get(field_key ?? '');
      const multiple = takesList(field, operator);
      if (multiple && !Array.isArray(value)) {
        control.setValue(isBlank(value) ? [] : [value]);
      } else if (!multiple && Array.isArray(value)) {
        const isUser =
          valueKindFor(field, operator) === ConditionValueKindEnum.USER;
        // The users lookup cannot drop picked users, so several users start it over empty.
        if (isUser && value.length > 1) {
          control.setValue(null);
          this.resetUserPicker(rule);
        } else {
          control.setValue(value[0] ?? null);
        }
      }
    }
    control.updateValueAndValidity();
  }
}
